import { assetUrl, isBlobstoreResolved } from '@/utils/assetUrls';
import type { ResolvedAssets, UploadedAsset } from '@/types/api/assets';
import type { FileKind } from '@/designer/designer';

/** Cloudinary transcodes from the path extension; a Blobstore signed URL is fetched exactly as signed. */
const toPngUrl = (url: string): string => {
  const parsed = new URL(url);
  parsed.pathname = parsed.pathname.replace(/\.[^/.]+$/, '.png');
  return parsed.toString();
};

// Some callers pass a signed URL without its asset id; rewriting its path would break the signature.
const isSignedUrl = (url: string): boolean => new URL(url).searchParams.has('X-Amz-Signature');

const toPngBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type === 'image/png') {
    return blob;
  }

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise((resolve, reject) =>
    canvas.toBlob((png) => (png ? resolve(png) : reject(new Error('PNG conversion failed'))), 'image/png'),
  );
};

export const copyImage = async (file: UploadedAsset | FileKind, resolved: ResolvedAssets) => {
  if (file?.type !== 'image') {
    return;
  }

  const url = assetUrl(file, resolved);
  if (!url) {
    return;
  }

  const fetchUrl = isBlobstoreResolved(file, resolved) || isSignedUrl(url) ? url : toPngUrl(url);

  const makeImagePromise = async () => {
    const response = await fetch(fetchUrl);
    return toPngBlob(await response.blob());
  };

  const clipboardItem = new ClipboardItem({
    'image/png': makeImagePromise(),
  });

  await navigator.clipboard.write([clipboardItem]);
};
